import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { FieldTree, FormField } from "@angular/forms/signals";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountSelectionService } from "@front/m19-services";
import { Marketplaces } from "@front/m19-models";
import { IFormFieldComponent, IInputDirective, ILabelComponent } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  AlgoModeConfig,
  AlgoModeSelectionComponent,
} from "@m19-board/shared/algo-mode-selection/algo-mode-selection.component";

export interface SdCreateStrategySetupModel {
  strategyName: string;
  algoModeConfig: AlgoModeConfig | null;
}

@Component({
  selector: "app-sd-create-strategy-setup",
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    IFormFieldComponent,
    IInputDirective,
    ILabelComponent,
    FormField,
    AlgoModeSelectionComponent,
  ],
  template: ` <div class="flex flex-col gap-6 py-8" *transloco="let t">
    <div class="flex flex-col gap-1">
      <h4>{{ t("sd-strategy-creation.sponsored_display_strategy") }}</h4>
    </div>
    <IFormField>
      <label iLabel>{{ t("sd-strategy-creation.strategy_name") }}</label>
      <input iInput [formField]="setup().strategyName" autoFocus data-testid="strategy-name" size="sm" />
    </IFormField>
    <div class="flex flex-col gap-1">
      <h4>{{ t("sd-strategy-creation.algorithm") }}</h4>
    </div>
    <app-algo-mode-selection
      class="w-full"
      [selectedAlgoModeConfig]="setup().algoModeConfig().value() ?? undefined"
      (algoModeConfig)="setAlgoModeConfig($event)"
      [currencyCode]="currencyCode()"
    />
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdCreateStrategySetupComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);

  readonly setup = input.required<FieldTree<SdCreateStrategySetupModel>>();

  private readonly selectedAccount = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  currencyCode = computed(() => Marketplaces[this.selectedAccount().marketplace].currency);

  setAlgoModeConfig(algoModeConfig: Partial<AlgoModeConfig>) {
    this.setup()
      .algoModeConfig()
      .value.set(algoModeConfig as AlgoModeConfig);
  }
}
